import React from 'react'
import styled from 'styled-components'
import { useAppContext } from '~/app-context'

const StatValue = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

export const ActiveTimeDisplay: React.FC<{ initialSeconds: number }> = ({
    initialSeconds,
}) => {
    const { globalEvents } = useAppContext()
    const [displaySeconds, setDisplaySeconds] = React.useState(initialSeconds)
    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(
        null,
    )
    const startTimeRef = React.useRef<number | null>(null)
    const accumulatedRef = React.useRef<number>(initialSeconds)

    React.useEffect(() => {
        const saved = localStorage.getItem('split-tabs-active-time')
        const savedSeconds = saved ? Number.parseInt(saved, 10) : initialSeconds
        const initialValue = Math.max(savedSeconds, initialSeconds)
        accumulatedRef.current = initialValue
        setDisplaySeconds(initialValue)

        intervalRef.current = setInterval(() => {
            if (startTimeRef.current !== null && !document.hidden) {
                const now = Date.now()
                const elapsed = (now - startTimeRef.current) / 1000
                const total = accumulatedRef.current + elapsed
                setDisplaySeconds(Math.floor(total))
            }
        }, 1000)

        let visibilityStartTime: number | null = null
        let visibilityTimeout: ReturnType<typeof setTimeout> | null = null
        let idleTimeout: ReturnType<typeof setTimeout> | null = null
        let lastActivityTime = Date.now()

        const handleVisibilityChange = () => {
            const now = Date.now()
            if (document.hidden) {
                if (
                    startTimeRef.current !== null &&
                    visibilityStartTime !== null
                ) {
                    const timeSinceVisibility = now - visibilityStartTime
                    if (timeSinceVisibility > 3000) {
                        const elapsed = (now - startTimeRef.current) / 1000
                        accumulatedRef.current += elapsed
                        setDisplaySeconds(Math.floor(accumulatedRef.current))
                    }
                    startTimeRef.current = null
                    visibilityStartTime = null
                }
                if (visibilityTimeout) clearTimeout(visibilityTimeout)
                if (idleTimeout) clearTimeout(idleTimeout)
            } else {
                visibilityStartTime = now
                visibilityTimeout = setTimeout(() => {
                    startTimeRef.current = Date.now()
                    lastActivityTime = Date.now()
                    idleTimeout = setTimeout(() => {
                        if (startTimeRef.current !== null) {
                            const elapsed =
                                (lastActivityTime - startTimeRef.current) / 1000
                            accumulatedRef.current += elapsed
                            setDisplaySeconds(
                                Math.floor(accumulatedRef.current),
                            )
                            startTimeRef.current = null
                            visibilityStartTime = null
                        }
                    }, 5000)
                }, 3000)
            }
        }

        const handleActivity = () => {
            if (document.hidden) return
            const now = Date.now()
            if (idleTimeout) clearTimeout(idleTimeout)
            if (
                visibilityStartTime !== null &&
                startTimeRef.current === null &&
                now - visibilityStartTime > 3000
            ) {
                startTimeRef.current = now
            }
            lastActivityTime = now
            idleTimeout = setTimeout(() => {
                if (startTimeRef.current !== null) {
                    const elapsed =
                        (lastActivityTime - startTimeRef.current) / 1000
                    accumulatedRef.current += elapsed
                    setDisplaySeconds(Math.floor(accumulatedRef.current))
                    startTimeRef.current = null
                }
            }, 5000)
        }

        if (!document.hidden) {
            visibilityStartTime = Date.now()
            visibilityTimeout = setTimeout(() => {
                startTimeRef.current = Date.now()
                lastActivityTime = Date.now()
                idleTimeout = setTimeout(() => {
                    if (startTimeRef.current !== null) {
                        const elapsed =
                            (lastActivityTime - startTimeRef.current) / 1000
                        accumulatedRef.current += elapsed
                        setDisplaySeconds(Math.floor(accumulatedRef.current))
                        startTimeRef.current = null
                        visibilityStartTime = null
                    }
                }, 5000)
            }, 3000)
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        document.addEventListener('mousemove', handleActivity)
        document.addEventListener('keydown', handleActivity)
        document.addEventListener('click', handleActivity)
        document.addEventListener('scroll', handleActivity)

        const handleInvoiceAdded = () => {
            accumulatedRef.current += 10
            setDisplaySeconds(Math.floor(accumulatedRef.current))
            localStorage.setItem(
                'split-tabs-active-time',
                accumulatedRef.current.toString(),
            )
        }

        const unsubscribeInvoiceAdded = globalEvents?.listen((event) => {
            if (event.type === 'invoiceAdded') {
                handleInvoiceAdded()
            }
        })

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            )
            document.removeEventListener('mousemove', handleActivity)
            document.removeEventListener('keydown', handleActivity)
            document.removeEventListener('click', handleActivity)
            document.removeEventListener('scroll', handleActivity)
            if (visibilityTimeout) clearTimeout(visibilityTimeout)
            if (idleTimeout) clearTimeout(idleTimeout)
            unsubscribeInvoiceAdded?.()
        }
    }, [initialSeconds, globalEvents])

    const formatActiveTime = (seconds: number): string => {
        const totalMinutes = Math.floor(seconds / 60)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60

        if (totalMinutes < 1) {
            return '<1m'
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`
        } else {
            return `${minutes}m`
        }
    }

    return <StatValue>{formatActiveTime(displaySeconds)}</StatValue>
}
